import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';

import type { Theme } from '@ui/ThemeRegistry/theme.types';

const defaultProps: ComponentsProps['HeaderNavLink'] = {};

const styleOverrides: ComponentsOverrides<Theme>['HeaderNavLink'] = {
  root: ({ theme }) => ({
    ...theme.typography.body1,

    'display': 'flex',
    'flexDirection': 'column',
    'flexGrow': '1',
    'position': 'relative',
    'fontWeight': 900,

    [theme.breakpoints.up('md')]: {
      height: '100%'
    },

    '&::after': {
      content: '""',
      position: 'absolute',
      top: '100%',
      left: 0,
      width: '100%',
      height: '20px',
      display: 'block',
      zIndex: 999
    },

    '&:hover, &:focus, &:focus-within': {
      '[class*="HeaderNavLink-navItemLink"]': {
        'color': 'var(--mui-palette-accent-contrastText)',
        'backgroundColor': 'var(--mui-palette-accent-main)',
        'textDecoration': 'none!important',
        'borderRadius': theme.shape.borderRadius,
        '.MuiSvgIcon-root': {
          transform: 'rotate(-90deg)'
        }
      },

      '[class*="HeaderNavLink-navItemSubMenu"]': {
        visibility: 'visible',
        opacity: 1,
        maxHeight: '80vh',
        maxWidth: '80vw',
        width: '100vw',
        transition: 'visibility 0s, opacity 0.3s ease, max-height 0.3s ease'
      }
    }
  }),

  navItemLink: ({ theme, open }) => ({
    'flexGrow': '1',
    'alignItems': 'center',
    'display': 'inline-flex',
    'width': '100%',
    'justifyContent': 'space-between',
    'cursor': 'pointer',
    'height': '100%',
    'fontWeight': 900,
    'textTransform': 'none',
    'textDecoration': 'none !important',
    'transition': 'background-color 0.3s ease, color 0.3s ease',
    'padding': 'var(--grid-gap-half)',
    'borderRadius': theme.shape.borderRadius,
    'backgroundColor': 'transparent',

    '&:hover': {
      'color': 'var(--mui-palette-accent-contrastText)',
      'backgroundColor': 'var(--mui-palette-accent-main)',
      'textDecoration': 'none!important',
      '.MuiSvgIcon-root': {
        transform: 'rotate(-90deg)'
      }
    },

    [theme.breakpoints.up('md')]: {
      padding: 'var(--grid-gap-quarter)',
      justifyContent: 'flex-start',
      marginTop: 'calc(-0.5 * var(--grid-gap-quarter)) !important',
      marginBottom: 'calc(-0.5 * var(--grid-gap-quarter)) !important'
    },

    '.MuiSvgIcon-root': {
      fill: theme.vars.palette.primary.main,
      width: 'auto',
      height: 'var(--grid-gap-half)',
      marginLeft: 'var(--grid-gap-half)',
      transform: 'rotate(90deg)',
      transition:
        'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

      ...(!!open && {
        [theme.breakpoints.down('md')]: {
          transform: 'rotate(-90deg)'
        }
      }),

      [theme.breakpoints.up('md')]: {
        height: '10px',
        fill: theme.vars.palette.primary.main
      }
    }
  }),

  navItemSubMenu: ({ theme, ownerState }) => ({
    'position': 'absolute',
    'top': 'calc(100% + 10px)',
    'left': '50%',
    'transform': 'translateX(-50%)',
    'visibility': 'hidden',
    'opacity': 0,
    'maxHeight': 0,
    'transition': 'visibility 0.3s, opacity 0.3s ease, max-height 0.3s ease',
    'zIndex': 1000,
    'boxShadow': theme.shadows[2],
    'borderRadius': theme.shape.borderRadius,
    'overflow': 'hidden',
    'display': 'flex',
    'flexDirection': 'row',
    'justifyContent': 'flex-start',
    'alignItems': 'flex-start',
    'gap': 'var(--grid-gap-half)',
    'padding': 'var(--grid-gap-half)',
    'width': 'fit-content',
    'maxWidth': 'min(100vw - var(--grid-gap-double), 80vw)',

    [theme.breakpoints.up('md')]: {
      'padding': 'var(--grid-gap-half)',
      'left': '50%',
      'transform': 'translateX(-50%)',
      '&': {
        left: 'clamp(var(--grid-gap), 50%, calc(100vw - 100% - var(--grid-gap)))',
        transform: 'translateX(-50%)'
      }
    },

    '&:hover': {
      visibility: 'visible',
      opacity: 1,
      maxHeight: '100vh'
    },

    '& [data-variant="group"]': {
      pointerEvents: 'none'
    },

    '& [data-variant="group"] + *': {
      pointerEvents: 'auto'
    },

    '& [data-variant]': {
      backgroundColor: 'transparent !important'
    }
  }),

  navItemSubMenuItem: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'padding': 'var(--grid-gap-double) var(--grid-gap)',
    'borderRadius': theme.shape.borderRadius,
    'transition': 'background-color 0.3s ease',
    'backgroundColor': 'transparent !important',
    'gap': 'var(--grid-gap-double)',

    '& [data-variant="group"], & [data-variant="group"] *': {
      pointerEvents: 'none !important',
      cursor: 'default !important',
      backgroundColor: 'transparent !important'
    },

    '& > div': {
      'borderRadius': theme.shape.borderRadius,
      'transition': 'background-color 0.3s ease',
      'padding': 'var(--grid-gap-half)',
      'backgroundColor': 'transparent !important',

      '&:hover': {
        'backgroundColor': 'var(--mui-palette-accent-main) !important',
        '& [data-variant="linkNested"]': {
          color: 'var(--mui-palette-accent-contrastText)',
          backgroundColor: 'transparent !important',
          textDecoration: 'none !important'
        }
      }
    },

    '& [data-variant="linkNested"], & [data-variant="linkNested"] *': {
      pointerEvents: 'auto !important',
      cursor: 'pointer !important',
      textDecoration: 'none !important',
      fontWeight: 900,
      backgroundColor: 'transparent !important'
    },

    '& > [data-variant="group"]': {
      'margin': 0,
      'padding': 0,

      '& > a': {
        ...theme.typography.bodyLarge,
        'margin': 0,
        'padding': 0,

        '& *': {
          margin: 0,
          padding: 0
        }
      }
    },

    [theme.breakpoints.up('md')]: {
      width: 'auto'
    }
  }),

  navItemGroup: ({ theme }: { theme: Theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'backgroundColor': 'transparent !important',

    '& > div': {
      marginBottom: 'var(--grid-gap-half)',
      backgroundColor: 'transparent !important'
    }
  }),

  featuredItemWrap: ({ theme }) => ({
    'alignItems': 'flex-start',
    'gap': 'var(--grid-gap-half)',
    '& *': {
      textAlign: 'left'
    }
  }),

  featuredItem: ({ theme }) => ({
    // 'display': 'flex',
    // 'flexDirection': 'row',
    // 'alignItems': 'flex-start',
    // 'gap': 'var(--grid-gap-half)',
    // 'width': '100%',
    // 'padding': 'var(--grid-gap-half)',
    // 'borderRadius': 'var(--grid-gap-half)',
    // 'transition': 'background-color 0.2s ease',
    // 'textDecoration': 'none !important',
    // 'fontWeight': 900,
    // 'backgroundColor': 'transparent !important',
    // '&:hover': {
    //   backgroundColor: 'var(--mui-palette-accent-main)',
    //   color: 'var(--mui-palette-accent-contrastText)'
    // }
  }),

  featuredImage: ({ theme }) => ({
    'flexShrink': 0,
    'width': '48px',
    'height': '48px',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    }
  }),

  featuredSummary: ({ theme }) => ({
    'fontSize': '0.875rem',
    'color': theme.palette.text.secondary,
    'maxWidth': '50vw',
    'whiteSpace': 'normal',
    'overflow': 'visible',
    'textOverflow': 'ellipsis',
    'display': '-webkit-box',
    'WebkitLineClamp': 2,
    'WebkitBoxOrient': 'vertical',
    'lineHeight': '1.4',

    '& p': {
      margin: 0,
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  })
};

const createVariants = (_theme: Theme): ComponentsVariants['HeaderNavLink'] => [];

export const headerNavLinkTheme = (theme: Theme): ThemeOptions => ({
  components: {
    HeaderNavLink: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default headerNavLinkTheme;
